import React, { ChangeEvent, useEffect, useState } from 'react';
import {
    makeStyles,
    Theme,
    Grid,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    FormControlProps,
    TextField,
    TextFieldProps,
} from '@material-ui/core';
import { ITradeInVehicle } from './types';
import { useTranslation } from 'react-i18next';
import useMake from 'src/hooks/useMake';
import useAuth from 'src/hooks/useAuth';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import useVehicle from 'src/hooks/useVehicle';
import { TTradeInVehicle } from 'src/interfaces/evaluation';
import { DEFAULT_TRADE_IN_VEHICLE } from './constants';
import { IMake } from 'src/interfaces/make';
import { CapitalizeNames as capitalize } from 'src/utils/capitalize';
import { IVehicle } from 'src/interfaces/vehicle';
import PriceMask from 'src/utils/masks/NumberFormatPrice';
import NumberFormatYear from 'src/utils/masks/NumberFormatYear';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        marginTop: theme.spacing(1),
    },
}));

export const TradeInVehicle = ({
    vehicle,
    handleChangeVehicle,
    isEditable,
    ...props
}: ITradeInVehicle) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { getMakes, makes } = useMake();
    const { getVehicles, vehicles } = useVehicle();
    const { user } = useAuth();
    const [tradeInVehicle, setTradeInVehicle] = useState<TTradeInVehicle>(
        DEFAULT_TRADE_IN_VEHICLE
    );

    useEffect(() => {
        if (user && user.accessLevel) {
            if (user?.accessLevel === DEVELOPER_LEVEL_ACCESS) getMakes();
            else getMakes({ group: user?.group?._id });
        }
    }, [user]);

    useEffect(() => {
        if (tradeInVehicle?.make?.id)
            getVehicles({ make: tradeInVehicle?.make?.id });
    }, [tradeInVehicle?.make?.id]);

    useEffect(() => {
        if (vehicle) {
            setTradeInVehicle({
                make: { id: vehicle?.make?.id, name: vehicle?.make?.name },
                model: vehicle?.model,
                year: vehicle?.year,
                clientPrice: vehicle?.clientPrice,
                storePrice: vehicle?.storePrice,
            });
        }
    }, [vehicle]);

    const FORM_CONTROL_PROPS: FormControlProps = {
        variant: 'outlined',
        fullWidth: true,
        size: 'small',
        required: true,
        disabled: !isEditable,
    };

    const TEXTFIELD_PROPS: TextFieldProps = {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
        required: true,
        disabled: !isEditable,
    };

    const handleChangeTrade = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const updatedVehicle = {
            ...tradeInVehicle,
            [name]: value,
        };
        setTradeInVehicle(updatedVehicle);
        handleChangeVehicle(name, value);
    };

    return (
        <Grid container spacing={2} className={classes.root} {...props}>
            <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" {...FORM_CONTROL_PROPS}>
                    <InputLabel id="select-label">
                        {t('evaluation.tradeInVehicleObj.make')}
                    </InputLabel>
                    <Select
                        labelId="select-label"
                        input={
                            <OutlinedInput
                                label={t('evaluation.tradeInVehicleObj.make')}
                            />
                        }
                        onChange={(event) => {
                            const selectedMake = makes?.find(
                                (make: IMake) => make._id === event.target.value
                            );

                            if (selectedMake) {
                                const updatedVehicle = {
                                    ...tradeInVehicle,
                                    make: {
                                        id: selectedMake?._id,
                                        name: selectedMake?.name,
                                    },
                                };
                                setTradeInVehicle(updatedVehicle);
                                handleChangeVehicle(
                                    'make',
                                    updatedVehicle.make
                                );
                            }
                        }}
                        value={tradeInVehicle?.make?.id || ''}
                        name="make"
                    >
                        <MenuItem key={' '} value={' '}>
                            {String(t('AdvancedAll.Makes'))}
                        </MenuItem>
                        {makes?.map((make: IMake) => (
                            <MenuItem key={make._id} value={make._id}>
                                {capitalize(make.name)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormControl {...FORM_CONTROL_PROPS}>
                    <InputLabel id="select-label">
                        {t('evaluation.tradeInVehicleObj.model')}
                    </InputLabel>
                    <Select
                        labelId="select-label"
                        input={
                            <OutlinedInput
                                label={t('evaluation.tradeInVehicleObj.model')}
                            />
                        }
                        onChange={(event) => {
                            const updatedVehicle = {
                                ...tradeInVehicle,
                                model: event.target.value,
                            };
                            setTradeInVehicle(updatedVehicle);
                            handleChangeVehicle('model', updatedVehicle.model);
                        }}
                        value={tradeInVehicle?.model || ''}
                        name="model"
                    >
                        <MenuItem key={' '} value={' '}>
                            {String(t('AdvancedAll.Models'))}
                        </MenuItem>
                        {vehicles?.map((vehicle: IVehicle) => (
                            <MenuItem key={vehicle._id} value={vehicle._id}>
                                {capitalize(vehicle.model)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    value={tradeInVehicle?.year || 0}
                    onChange={handleChangeTrade}
                    name="year"
                    label={t('evaluation.tradeInVehicleObj.year')}
                    {...TEXTFIELD_PROPS}
                    InputProps={{ inputComponent: NumberFormatYear }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    value={tradeInVehicle?.clientPrice || 0}
                    onChange={handleChangeTrade}
                    name="clientPrice"
                    label={t('evaluation.tradeInVehicleObj.clientPrice')}
                    {...TEXTFIELD_PROPS}
                    InputProps={{
                        inputComponent: PriceMask,
                        autoComplete: 'off',
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    value={tradeInVehicle?.storePrice || 0}
                    onChange={handleChangeTrade}
                    name="storePrice"
                    label={t('evaluation.tradeInVehicleObj.storePrice')}
                    {...TEXTFIELD_PROPS}
                    InputProps={{
                        inputComponent: PriceMask,
                        autoComplete: 'off',
                    }}
                />
            </Grid>
        </Grid>
    );
};
